import { version as reactVersion } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import { browserMetrics } from '@atlassian/browser-metrics';
import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getBuildInfo } from '@confluence/build-info';
import { getMark } from '@confluence/performance/entry-points/performance.ts';
import type { SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { getLogger } from '@confluence/logger';

import { bundleEvalTimings } from './plugins/bundleEvalTimings.ts';
import { getCustomEventConfigs } from './utils/index.ts';
import { startLightHouseObservers } from './plugins/lighthouse-metrics/index.tsx';

let isBrowserMetricsInitialized = false;

const logger = getLogger('initializeBrowserMetrics');
const criticalAssetsList = [
	'app',
	'atl',
	'runtime',
	'vendors',
	'ssr-scripts',
	'stable_vendors',
	'query-preloader',
	'browser-not-supported',
];

function mapResources(url: string) {
	return criticalAssetsList.find((asset) => url.includes(`/${asset}.`)) || 'ignored';
}

/**
 * Initializes the Browser Metrics library with Confluence specific configurations.
 *
 * Should be called before any metrics are fired.
 */
export const initializeBrowserMetrics = (sessionData: SessionDataType): void => {
	if (!isBrowserMetricsInitialized) {
		const { shard } = sessionData;

		const getAnalyticsWebClientPromise = getAnalyticsWebClient();
		const product = isEmbeddedConfluence_DO_NOT_USE() ? 'embeddedConfluence' : 'confluence';

		import(/* webpackChunkName: "loadable-initializeUFOPublisher" */ './initializeUFOPublisher.ts')
			.then((ufo) => ufo.setup(sessionData, getAnalyticsWebClientPromise))
			.catch((error) => {
				logger.error`Fail to import UFO client: ${error}`;
			});

		startLightHouseObservers();

		browserMetrics.init({
			info: {
				app: {
					framework: {
						name: 'react',
						version: reactVersion,
					},
					version: {
						web: getBuildInfo().FRONTEND_VERSION,
						ssr: window.__SSR_BUILD__,
					},
				},
				hostname: window.location.hostname,
				product,
				population: StatsigConfigurations.getString('cc_ufo_group', 'unknown'),
				region: shard,
			},
			plugins: {
				featureFlags: {
					client: Promise.resolve({
						getValue: (featureFlagKey: string) => {
							// Using `FeatureGates.checkGate` here to avoid triggering exposure tracking
							// eslint-disable-next-line confluence-feature-gating/use-recommended-utils
							return FeatureGates.checkGate(featureFlagKey);
						},
					}),
				},
				resourceTimings: {
					mapResources,
					// Not interested in fetch requests as we have already marked wf/
					sanitiseEndpoints: () => 'ignored',
				},
				bundleEvalTimings,
			},
			endpoints: {
				eventPipelineClient: getAnalyticsWebClientPromise,
			},
			events: getCustomEventConfigs(sessionData),
			ssr: {
				getDoneMark: () => getMark('CFP-63.ssr-ttr') ?? null,
				getTimings: () => (window as any).__SSR_MEASURES__ || {},
			},
			enableReactUFOTracker: true,
		});

		isBrowserMetricsInitialized = true;
	}
};
